import {
  SearchIcon as DefaultSearchIcon,
  XIcon as DefaultXIcon,
} from "@heroicons/react/solid";
import clsx from "clsx";
import { ChangeEventHandler, KeyboardEvent, SVGProps, useMemo } from "react";

import { TextField } from "@/features/common/components/form/TextField";

const variantClassName = {
  default: {
    border: "border-gray-400 divide-gray-400",
    container: "rounded-full w-max",
    icon: "text-gray-700",
  },
  light: {
    border: "border-gray-200 divide-gray-100",
    container: "rounded-lg w-full",
    icon: "text-primary",
  },
};

export type FilterFieldProps = {
  variant?: "default" | "light";
  label: string;
  value?: string;
  isActive?: boolean;
  full?: boolean;
  onSearch?: () => void;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onClear?: () => void;
  SearchIcon?: (props: SVGProps<SVGSVGElement>) => JSX.Element;
  ClearSearchIcon?: (props: SVGProps<SVGSVGElement>) => JSX.Element;
};

export const SearchTextInput = ({
  variant = "default",
  label,
  value,
  isActive = false,
  full = false,
  onChange,
  onSearch = () => {},
  onClear = () => {},
  SearchIcon = DefaultSearchIcon,
  ClearSearchIcon = DefaultXIcon,
}) => {
  const isInputEmpty = useMemo(() => !value, [value]);

  const iconClasses = useMemo(
    () =>
      clsx(
        "h-4 w-4",
        isActive ? "text-primary" : variantClassName[variant].icon,
        !isInputEmpty ? "cursor-pointer" : "",
      ),
    [isActive, isInputEmpty, variant],
  );

  return (
    <div
      className={clsx(
        "min-w-72 flex items-center divide-x border bg-white px-4 py-2",
        isActive
          ? "border-primary ring-primary divide-primary"
          : variantClassName[variant].border,
        full ? "w-full rounded-full" : variantClassName[variant].container,
      )}
    >
      <div className="pr-2">
        {isInputEmpty ? (
          <SearchIcon className={iconClasses} />
        ) : (
          <ClearSearchIcon className={iconClasses} onClick={onClear} />
        )}
      </div>

      <div className="w-full pl-2">
        <TextField
          placeholder={`搜尋${label}`}
          value={value ?? ""}
          variant="text"
          inputClassName={clsx("font-light !py-0 !px-0")}
          // @todo refactor onChange prop type
          onChange={
            onChange as ChangeEventHandler<HTMLInputElement> &
              ChangeEventHandler<HTMLTextAreaElement>
          }
          onKeyDown={(
            e: KeyboardEvent<HTMLInputElement> &
              KeyboardEvent<HTMLTextAreaElement>,
          ) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onSearch();
            }
          }}
        />
      </div>
    </div>
  );
};
